/**
 * Hidden free-pass landing page for the full diagnostic.
 *
 * /freediagnostic?key=<FREE_PASS_KEY>
 *
 * Gated by a URL key (same security model as /test-pay-rs1) — 404 on mismatch
 * and `noindex` so the URL never leaks via search engines.
 *
 * The visitor fills a Name + Mobile + Email form. On submit:
 *   1) `/api/payments/free-pass` mints a real 30-day diagnostic access token
 *      with the captured name/mobile/email attached.
 *   2) `/api/diagnostic/start` is hit immediately so the row is upserted into
 *      MongoDB `diagnostic_reports` AND the Google Sheet `S1S2S3results`
 *      BEFORE the user begins the assessment. This guarantees lead capture
 *      even if the visitor bounces from /diagnostic without answering.
 *   3) Redirects to `/diagnostic?token=<token>`.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

const STYLE_PAGE = {
  minHeight: "100vh",
  background: "#F5F0E8",
  fontFamily: "'DM Sans', sans-serif",
  color: "#1C1714",
  padding: "3.5rem 1.5rem",
};

const STYLE_CARD = {
  maxWidth: 480,
  margin: "0 auto",
  background: "#fff",
  border: "1px solid #E5E1D6",
  borderRadius: 12,
  padding: "2rem 1.75rem",
  boxShadow: "0 14px 32px rgba(28,23,20,0.06)",
};

const STYLE_LABEL = {
  display: "block",
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "#5F5E5A",
  marginBottom: "0.4rem",
};

const STYLE_INPUT = {
  width: "100%",
  padding: "0.75rem 0.9rem",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "1rem",
  color: "#1C1714",
  background: "#F8F5EF",
  border: "1px solid #D3D1C7",
  borderRadius: 8,
  outline: "none",
  boxSizing: "border-box",
};

const STYLE_BUTTON = {
  width: "100%",
  padding: "0.9rem 1.25rem",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "1rem",
  fontWeight: 700,
  color: "#fff",
  background: "#2F7D57",
  border: "1px solid #2F7D57",
  borderRadius: 10,
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(47,125,87,0.22)",
};

export default function FreeDiagnosticPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const key = params.get("key") || "";

  // "gate" = checking key, "form" = showing form, "submitting" = posting to backend,
  // "denied" = 404, "error" = backend error
  const [gateStatus, setGateStatus] = useState("gate");
  const [errorMsg, setErrorMsg] = useState("");

  const [name, setName] = useState(params.get("name") || "");
  const [mobile, setMobile] = useState(params.get("mobile") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Tag the page as noindex regardless of state.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Free Diagnostic • Pivotbox";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  // Quick frontend gate (the backend re-validates the key).
  useEffect(() => {
    if (!key) {
      setGateStatus("denied");
      return;
    }
    setGateStatus("form");
  }, [key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const sanitizedMobile = mobile.replace(/\D/g, "");
    if (trimmedName.length < 2) { setFormError("Please enter your full name."); return; }
    if (sanitizedMobile.length < 10) { setFormError("Please enter a valid 10-digit mobile number."); return; }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setFormError("Please enter a valid email (or leave it blank).");
      return;
    }
    setFormError("");
    setSubmitting(true);

    const API_URL = process.env.REACT_APP_BACKEND_URL;

    try {
      // 1. Mint the free-pass access token with name/mobile/email attached.
      const tokenQs = new URLSearchParams({
        key,
        name: trimmedName,
        mobile: sanitizedMobile,
        email: trimmedEmail,
      });
      const tokenRes = await fetch(`${API_URL}/api/payments/free-pass?${tokenQs.toString()}`);
      if (!tokenRes.ok) {
        if (tokenRes.status === 404) { setGateStatus("denied"); return; }
        const j = await tokenRes.json().catch(() => ({}));
        setErrorMsg(j.detail || "Could not generate access. Please retry.");
        setGateStatus("error");
        return;
      }
      const tokenData = await tokenRes.json();
      const accessToken = tokenData.access_token;

      // 2. Write directly to Mongo + Google Sheet (S1S2S3results) so the lead
      //    is captured immediately, before the visitor even starts answering.
      try {
        await fetch(`${API_URL}/api/diagnostic/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: `free_${(accessToken || "").slice(0, 24)}`,
            mobile: sanitizedMobile,
            name: trimmedName,
            email: trimmedEmail,
            source: "free_pass",
            access_token: accessToken || "",
          }),
        });
      } catch (writeErr) {
        // Don't block the user if sheet write fails — they can still take the assessment.
        console.warn("diagnostic/start write failed:", writeErr);
      }

      // 3. Clear any previous DiagnosticPage state for this mobile so old
      //    answers from a prior test on this device don't carry over into
      //    the new session.
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.removeItem(`pivotbox_diag_v2_${sanitizedMobile}`);
          window.localStorage.removeItem(`pivotbox_diag_${sanitizedMobile}`);
          window.localStorage.removeItem("pivotbox_diag_session_id");
        }
      } catch { /* ignore */ }

      // 4. Redirect to the diagnostic, already unlocked.
      navigate(`/diagnostic?token=${encodeURIComponent(accessToken)}`, { replace: true });
    } catch (err) {
      setErrorMsg(String(err && err.message ? err.message : err));
      setGateStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  if (gateStatus === "denied") {
    return (
      <div style={{ ...STYLE_PAGE, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="free-diagnostic-blocked">
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", color: "#1C1714", fontSize: "2rem", margin: "0 0 0.5rem" }}>404</h1>
          <p style={{ margin: 0, color: "#5F5E5A" }}>Page not found.</p>
          <Link to="/" style={{ color: "#2F7D57", marginTop: "1rem", display: "inline-block" }}>← Back to home</Link>
        </div>
      </div>
    );
  }

  if (gateStatus === "error") {
    return (
      <div style={{ ...STYLE_PAGE, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="free-diagnostic-error">
        <div style={{ textAlign: "center", maxWidth: 420 }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", color: "#1C1714", fontSize: "1.5rem", margin: "0 0 0.5rem" }}>
            Something went wrong.
          </h1>
          <p style={{ margin: "0 0 1rem", color: "#5F5E5A" }}>{errorMsg || "Please try again."}</p>
          <button type="button" onClick={() => setGateStatus("form")} style={{ ...STYLE_BUTTON, width: "auto", padding: "0.6rem 1.2rem" }}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={STYLE_PAGE} data-testid="free-diagnostic-page">
      <div style={STYLE_CARD}>
        <span
          style={{
            display: "inline-block",
            background: "#EAF3EE",
            color: "#2F7D57",
            padding: "0.3rem 0.7rem",
            borderRadius: 999,
            fontSize: "0.7rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 700,
            marginBottom: "1rem",
          }}
        >
          Founder Diagnostic • Free Access
        </span>

        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.7rem", margin: "0 0 0.5rem", color: "#1C1714", lineHeight: 1.2 }}>
          Start your diagnostic.
        </h1>
        <p style={{ fontSize: "0.95rem", lineHeight: 1.6, color: "#5F5E5A", margin: "0 0 1.5rem" }}>
          Enter your details to unlock the full Founder Diagnostic. We save your progress so you can return any time.
        </p>

        <form onSubmit={handleSubmit} data-testid="free-diagnostic-form">
          <div style={{ marginBottom: "1rem" }}>
            <label style={STYLE_LABEL} htmlFor="fd-name">Full name</label>
            <input
              id="fd-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
              autoComplete="name"
              placeholder="Aarav Mehta"
              style={STYLE_INPUT}
              data-testid="free-diagnostic-name-input"
            />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={STYLE_LABEL} htmlFor="fd-mobile">Mobile (10 digits)</label>
            <input
              id="fd-mobile"
              type="tel"
              inputMode="numeric"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              required
              disabled={submitting}
              autoComplete="tel"
              placeholder="9876543210"
              style={STYLE_INPUT}
              data-testid="free-diagnostic-mobile-input"
            />
          </div>

          <div style={{ marginBottom: "1.25rem" }}>
            <label style={STYLE_LABEL} htmlFor="fd-email">Email (optional)</label>
            <input
              id="fd-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              autoComplete="email"
              placeholder="you@example.com"
              style={STYLE_INPUT}
              data-testid="free-diagnostic-email-input"
            />
          </div>

          {formError && (
            <p style={{ color: "#b91c1c", fontSize: "0.85rem", margin: "0 0 1rem" }} data-testid="free-diagnostic-form-error">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{ ...STYLE_BUTTON, opacity: submitting ? 0.7 : 1, cursor: submitting ? "wait" : "pointer" }}
            data-testid="free-diagnostic-submit-button"
          >
            {submitting ? "Opening your diagnostic…" : "Start the Diagnostic →"}
          </button>
        </form>

        <p style={{ marginTop: "1.5rem", fontSize: "0.78rem", color: "#8a8278", textAlign: "center" }}>
          Your responses are private. We do not share them.
        </p>
      </div>
    </div>
  );
}
